import { API_BASE_URL } from "../config/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";

const emptyForm = {
  serviceName: "General Service",
  customerName: "",
  company: "",
  address: "",
  city: "",
  contactName: "",
  phone: "",
  email: "",
  serviceDate: "",
  nextServiceDate: "",
  stickerRequired: true,
  complaint: "",
  observation: "",
  actionTaken: "",
  notes: "",
  assignedEngineers: []
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const formatTeam = (value = "", dynamicTeams = []) => {
  if (!value) return "No Team";
  const matched = dynamicTeams.find(t => t.id === value || t.name === value);
  if (matched) return matched.name;
  if (value === "team_1") return "Team 1";
  if (value === "team_2") return "Team 2";
  if (value === "team_3") return "Team 3";
  return value;
};

const statusTone = (value = "") => {
  if (value === "Reports Merged") return "bg-emerald-100 text-emerald-700";
  if (value === "All Reports Submitted") return "bg-sky-100 text-sky-700";
  if (value === "Partially Completed") return "bg-amber-100 text-amber-700";
  if (value === "In Progress") return "bg-violet-100 text-violet-700";
  return "bg-stone-100 text-stone-700";
};

const reportStatusTone = (value = "") => {
  if (value === "Approved") return "bg-emerald-100 text-emerald-700";
  if (value === "Submitted") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-700";
};

function GeneralServicesContent() {
  const role = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const isAdmin = role === "admin";
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState("");
  const [mergeService, setMergeService] = useState(null);
  const [mergeSelection, setMergeSelection] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [dynamicTeams, setDynamicTeams] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers })
      .then(res => {
        if (res.data && Array.isArray(res.data.teams)) {
          setDynamicTeams(res.data.teams);
        } else {
          setDynamicTeams([
            { id: "team_1", name: "Team 1" },
            { id: "team_2", name: "Team 2" },
            { id: "team_3", name: "Team 3" }
          ]);
        }
      })
      .catch(err => {
        console.error("Error loading system settings teams:", err);
        setDynamicTeams([
          { id: "team_1", name: "Team 1" },
          { id: "team_2", name: "Team 2" },
          { id: "team_3", name: "Team 3" }
        ]);
      });
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const [servicesRes, engineersRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/general-services", {
          params: isAdmin ? undefined : { engineer: engineerLookup }
        }),
        isAdmin
          ? axios.get(API_BASE_URL + "/api/engineers").catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);

      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setEngineers(Array.isArray(engineersRes.data) ? engineersRes.data : []);
    } catch (error) {
      setServices([]);
      setEngineers([]);
    } finally {
      setLoading(false);
    }
  }, [engineerLookup, isAdmin]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const engineerOptions = useMemo(
    () =>
      engineers.map((engineer) => ({
        engineerId: engineer.engineerId || "",
        engineerName: engineer.name || "",
        engineerEmail: engineer.email || ""
      })),
    [engineers]
  );

  const openCreate = () => {
    setEditingServiceId("");
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (service) => {
    setEditingServiceId(service._id || "");
    setForm({
      serviceName: service.serviceName || "General Service",
      customerName: service.customerName || "",
      company: service.company || "",
      address: service.address || "",
      city: service.city || "",
      contactName: service.contactName || "",
      phone: service.phone || "",
      email: service.email || "",
      serviceDate: service.serviceDate || "",
      nextServiceDate: service.nextServiceDate || "",
      stickerRequired: true,
      complaint: service.complaint || "",
      observation: service.observation || "",
      actionTaken: service.actionTaken || "",
      notes: service.notes || "",
      assignedEngineers: Array.isArray(service.assignedEngineers) ? service.assignedEngineers : []
    });
    setShowForm(true);
  };

  const saveService = async () => {
    if (!form.customerName.trim() && !form.company.trim()) {
      alert("Enter customer or company name");
      return;
    }

    if (!Array.isArray(form.assignedEngineers) || form.assignedEngineers.length === 0) {
      alert("Assign at least one engineer");
      return;
    }

    try {
      setSaving(true);
      if (editingServiceId) {
        await axios.put(API_BASE_URL + `/api/general-services/${editingServiceId}`, form);
      } else {
        await axios.post(API_BASE_URL + "/api/general-services", form);
      }
      setShowForm(false);
      setEditingServiceId("");
      setForm(emptyForm);
      await fetchServices();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save general service");
    } finally {
      setSaving(false);
    }
  };

  const addAssignedEngineer = () => {
    setForm((current) => ({
      ...current,
      assignedEngineers: [
        ...current.assignedEngineers,
        { engineerId: "", engineerName: "", engineerEmail: "", team: "" }
      ]
    }));
  };

  const updateAssignedEngineer = (index, name, value) => {
    setForm((current) => ({
      ...current,
      assignedEngineers: current.assignedEngineers.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        if (name === "engineerId") {
          const matched = engineerOptions.find((engineer) => engineer.engineerId === value);
          return matched
            ? { ...item, ...matched }
            : { ...item, engineerId: value };
        }

        return { ...item, [name]: value };
      })
    }));
  };

  const removeAssignedEngineer = (index) => {
    setForm((current) => ({
      ...current,
      assignedEngineers: current.assignedEngineers.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const openMerge = (service) => {
    setMergeService(service);
    setMergeSelection((service.reports || []).map((report) => String(report._id)));
  };

  const proceedMerge = () => {
    if (!mergeService?._id) return;
    if (mergeSelection.length === 0) {
      alert("Select at least one engineer report to merge");
      return;
    }
    navigate(`/reports/merge/create?generalServiceId=${mergeService._id}&mergeReports=${mergeSelection.join(",")}`);
  };

  const handleServiceDateChange = (value) => {
    let nextDate = form.nextServiceDate;
    if (value) {
      const parts = value.split("-").map(Number);
      if (parts.length === 3) {
        const [y, m, d] = parts;
        const due = new Date(y, m - 1, d);
        due.setFullYear(due.getFullYear() + 1);
        due.setDate(due.getDate() - 1);
        const dy = due.getFullYear();
        const dm = String(due.getMonth() + 1).padStart(2, "0");
        const dd = String(due.getDate()).padStart(2, "0");
        nextDate = `${dy}-${dm}-${dd}`;
      }
    } else {
      nextDate = "";
    }
    setForm((current) => ({
      ...current,
      serviceDate: value,
      nextServiceDate: nextDate
    }));
  };

  const content = (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-950">{isAdmin ? "General Services" : "Assigned Services"}</h1>
            <p className="mt-1 text-sm text-slate-500">
              {isAdmin
                ? "Create one service, assign multiple engineers, review engineer reports, and merge them into one final report."
                : "View only the services assigned to you and create or update only your own reports."}
            </p>
          </div>
          {isAdmin ? (
            <button
              type="button"
              onClick={openCreate}
              className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white"
            >
              Create General Service
            </button>
          ) : null}
        </div>
      </section>

      {showForm && isAdmin ? (
        <section className="rounded-[2rem] border border-amber-100 bg-white p-6 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Input label="Service Name" value={form.serviceName} onChange={(value) => setForm((current) => ({ ...current, serviceName: value }))} />
            <Input label="Customer Name" value={form.customerName} onChange={(value) => setForm((current) => ({ ...current, customerName: value }))} />
            <Input label="Company" value={form.company} onChange={(value) => setForm((current) => ({ ...current, company: value }))} />
            <Input label="Service Date" type="date" value={form.serviceDate} onChange={handleServiceDateChange} />
            <Input label="Next Service Date" type="date" value={form.nextServiceDate} onChange={(value) => setForm((current) => ({ ...current, nextServiceDate: value }))} />
            <Input label="Contact Name" value={form.contactName} onChange={(value) => setForm((current) => ({ ...current, contactName: value }))} />
            <Input label="Phone" value={form.phone} onChange={(value) => setForm((current) => ({ ...current, phone: value }))} />
            <Input label="Email" value={form.email} onChange={(value) => setForm((current) => ({ ...current, email: value }))} />
            <Input label="City" value={form.city} onChange={(value) => setForm((current) => ({ ...current, city: value }))} />
            <div className="md:col-span-2 xl:col-span-3">
              <TextArea label="Address" value={form.address} onChange={(value) => setForm((current) => ({ ...current, address: value }))} rows={2} />
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <TextArea label="Service Details" value={form.complaint} onChange={(value) => setForm((current) => ({ ...current, complaint: value }))} rows={3} />
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <TextArea label="Notes" value={form.notes} onChange={(value) => setForm((current) => ({ ...current, notes: value }))} rows={2} />
            </div>
          </div>

          <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Assigned Engineers</h2>
              <button type="button" onClick={addAssignedEngineer} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-800 ring-1 ring-slate-200">
                Add Engineer
              </button>
            </div>

            <div className="space-y-3">
              {form.assignedEngineers.map((assignment, index) => (
                <div key={`assigned-${index}`} className="grid gap-3 rounded-2xl bg-white p-4 md:grid-cols-[minmax(0,1fr)_180px_120px]">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">Engineer</label>
                    <select
                      value={assignment.engineerId}
                      onChange={(event) => updateAssignedEngineer(index, "engineerId", event.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
                    >
                      <option value="">Select engineer</option>
                      {engineerOptions.map((engineer) => (
                        <option key={engineer.engineerId} value={engineer.engineerId}>
                          {engineer.engineerName || engineer.engineerId}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">Team</label>
                    <select
                      value={assignment.team}
                      onChange={(event) => updateAssignedEngineer(index, "team", event.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
                    >
                      <option value="">No Team</option>
                      {dynamicTeams.map((team) => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeAssignedEngineer(index)}
                      className="w-full rounded-2xl bg-rose-100 px-4 py-3 text-sm font-semibold text-rose-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700">
              Cancel
            </button>
            <button type="button" onClick={saveService} disabled={saving} className="rounded-2xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Saving..." : editingServiceId ? "Update Service" : "Save Service"}
            </button>
          </div>
        </section>
      ) : null}

      {loading ? (
        <section className="rounded-[2rem] border border-slate-200 bg-white px-6 py-14 text-center text-slate-500 shadow-sm">
          Loading services...
        </section>
      ) : services.length === 0 ? (
        <section className="rounded-[2rem] border border-slate-200 bg-white px-6 py-14 text-center text-slate-500 shadow-sm">
          No general services found.
        </section>
      ) : (
        <div className="space-y-5">
          {services.map((service) => {
            const myReport = !isAdmin
              ? (service.reports || []).find((report) =>
                  [report.reportOwnerEngineerId, report.reportOwnerEngineerName, report.reportOwnerEngineerEmail, report.engineer]
                    .filter(Boolean)
                    .some((value) =>
                      [localStorage.getItem("engineerId"), localStorage.getItem("engineerEmail"), engineerName]
                        .filter(Boolean)
                        .map((item) => String(item).trim().toLowerCase())
                        .includes(String(value).trim().toLowerCase())
                    )
                )
              : null;

            return (
              <section key={service._id} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-bold text-slate-950">{service.serviceId || "Service"}</h2>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(service.status)}`}>
                        {service.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-slate-800">{service.customerName || service.company || "-"}</p>
                    <p className="mt-1 text-sm text-slate-500">{service.serviceName || "General Service"}{service.serviceDate ? ` | ${formatDate(service.serviceDate)}` : ""}</p>
                    {service.complaint ? <p className="mt-3 text-sm text-slate-700">{service.complaint}</p> : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {isAdmin ? (
                      <>
                        <button type="button" onClick={() => openEdit(service)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
                          Edit Service
                        </button>
                        <button
                          type="button"
                          onClick={() => openMerge(service)}
                          disabled={Boolean(service.mergedReport) || (service.reports || []).length === 0}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="material-symbols-outlined text-base">call_merge</span>
                          Merge All Reports
                        </button>
                      </>
                    ) : myReport ? (
                      <>
                        <button type="button" onClick={() => navigate(`/my-reports/${myReport._id}`)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                          View My Report
                        </button>
                        {!myReport.mergeLocked ? (
                          <button type="button" onClick={() => navigate(`/create-report/${myReport._id}`)} className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white">
                            Edit My Report
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate(`/create-report?generalServiceId=${service._id}`)}
                        className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white"
                      >
                        Create My Report
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MiniStat label="Assigned Engineers" value={(service.assignedEngineers || []).length} />
                  <MiniStat label="Reports Submitted" value={(service.reports || []).length} />
                  <MiniStat label="Merged Report" value={service.mergedReport?.reportId || "-"} />
                  <MiniStat label="Contact" value={service.contactName || service.phone || "-"} />
                </div>

                <div className="mt-5 rounded-[1.5rem] bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Assigned Engineers</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(service.assignedEngineers || []).map((assignment, index) => (
                      <span key={`${assignment.engineerId}-${index}`} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
                        {(assignment.engineerName || assignment.engineerId || assignment.engineerEmail || "Engineer")} | {formatTeam(assignment.team, dynamicTeams)}
                      </span>
                    ))}
                  </div>
                </div>

                {(isAdmin ? true : Boolean(myReport)) ? (
                  <div className="mt-5">
                    <p className="text-sm font-bold text-slate-900">{isAdmin ? "Engineer Reports" : "My Report"}</p>
                    <div className="mt-3 space-y-3">
                      {(isAdmin ? (service.reports || []) : myReport ? [myReport] : []).map((report) => (
                        <div key={report._id} className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                              <p className="font-semibold text-slate-900">{report.reportId || report.visitId || "Report"}</p>
                              <p className="mt-1 text-sm text-slate-500">
                                {report.reportOwnerEngineerName || report.engineer || "-"} | {formatDate(report.visitDate || report.date)}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${reportStatusTone(report.engineerReportStatus || "Submitted")}`}>
                                {report.engineerReportStatus || "Submitted"}
                              </span>
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${reportStatusTone(report.approvalStatus === "Approved" ? "Approved" : "Draft")}`}>
                                {report.approvalStatus || "Approval Pending"}
                              </span>
                              <button type="button" onClick={() => navigate(isAdmin ? `/reports/${report._id}` : `/my-reports/${report._id}`)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">
                                View
                              </button>
                              {isAdmin ? (
                                <button type="button" onClick={() => navigate(`/reports/${report._id}/edit`)} className="rounded-xl border border-sky-300 px-3 py-2 text-sm font-semibold text-sky-700">
                                  Edit
                                </button>
                              ) : !report.mergeLocked ? (
                                <button type="button" onClick={() => navigate(`/create-report/${report._id}`)} className="rounded-xl border border-sky-300 px-3 py-2 text-sm font-semibold text-sky-700">
                                  Edit
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {isAdmin && service.mergedReport ? (
                  <div className="mt-5 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-bold text-emerald-900">Final Merged Report</p>
                        <p className="mt-1 text-sm text-emerald-700">{service.mergedReport.reportId || "-"} | {formatDate(service.mergedAt)}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate(`/reports/${service.mergedReport._id}`)} className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white">
                          View Final Report
                        </button>
                        <button type="button" onClick={() => navigate(`/reports/${service.mergedReport._id}/edit`)} className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700">
                          Edit Final Report
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      {mergeService ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="w-full max-w-2xl rounded-[2rem] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Merge Reports</h2>
                <p className="mt-1 text-sm text-slate-500">Select which engineer reports should be merged into one final report.</p>
              </div>
              <button type="button" onClick={() => setMergeService(null)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">
                Close
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {(mergeService.reports || []).map((report) => (
                <label key={report._id} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={mergeSelection.includes(String(report._id))}
                    onChange={(event) =>
                      setMergeSelection((current) =>
                        event.target.checked
                          ? [...current, String(report._id)]
                          : current.filter((item) => item !== String(report._id))
                      )
                    }
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{report.reportId || report.visitId || "Report"}</p>
                    <p className="mt-1 text-sm text-slate-500">{report.reportOwnerEngineerName || report.engineer || "-"} | {report.engineerReportStatus || "Submitted"}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setMergeService(null)} className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700">
                Cancel
              </button>
              <button type="button" onClick={proceedMerge} className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white">
                Merge Selected
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (!isAdmin) {
    return <EngineerLayout>{content}</EngineerLayout>;
  }

  return content;
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
      />
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-[1.5rem] bg-slate-50 px-4 py-4">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

export default function GeneralServices() {
  return <GeneralServicesContent />;
}
